import { isEqual, omit } from 'lodash-es'
import {
  LucideChevronUp,
  LucidePin,
  LucidePinOff,
  LucideSlidersHorizontal,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAdminCompaniesQuery, useAdminUsersQuery } from '~/api'
import {
  aDefaultMapFilters,
  aDrawnFeature,
  aFiltersPinned,
  aMapDrawIsOpen,
  aMapFilters,
  aMapZoomNextListingLoad,
  aOrganization,
  aPersistedFilters,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
  type MapFiltersType,
} from '~/atoms'
import { ControlTooltip, PulseDot } from '~/components'
import { Accordion, Button, Sheet } from '~/components/ui'
import { useBreakpoint, useEmbed, useIsSafari } from '~/hooks'
import { normalizeMapPropertySubtypeFilters } from '~/models/mapPropertyUse'
import { ModelStatus, UserRole } from '~/models'
import { cn } from '~/utils'
import { clearRememberedDrawnFeatureForBackNavigation } from '../draw'
import {
  LocationFilters,
  MapBrokersFilter,
  MapBusinessListingFilter,
  MapCompaniesFilter,
  MapDateFilter,
  MapIndustrialFilters,
  MapListingStatusFilter,
  MapLotSizeFilter,
  MapMinMaxFilter,
  MapPropertySubtypesFilter,
  MapPropertyTypesFilter,
  MapRateFilter,
  MapSalePriceFilter,
  MapSearchFilter,
  MapSort,
  MapTypeFilter,
} from './filters'

const INDUSTRIAL_FILTERS = [
  'clearHeight',
  'eaveHeight',
  'warehouseSf',
  'warehouseHvac',
  'officeSf',
  'officeCeilingHeight',
  'mezzanineSf',
  'mezzanineHvac',
  'dockNumber',
  'driveInNumber',
  'trailerParkingSpaces',
  'freightDockNumber',
  'overheadCranes',
  'railDoors',
  'yard',
  'yardFenced',
  'yardPaved',
]

const MULTIFAMILY_FILTERS = ['totalUnits']

function getObjectDiff(
  obj1: { [key: string]: any },
  obj2: { [key: string]: any }
) {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
      result.push(key)
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)
      result.splice(resultKeyIndex, 1)
    }
    return result
  }, Object.keys(obj2))

  return diff
}

function FilterAccordion({
  className,
  label,
  children,
  filters,
  keys,
}: {
  className?: string
  label: string
  children: React.ReactNode
  filters: MapFiltersType
  keys?: string[]
}) {
  const showDot = useMemo(() => {
    return !!keys && keys.some((key) => key in filters)
  }, [keys, filters])
  return (
    <Accordion.Item value={label.toLowerCase()} className={className}>
      <Accordion.Trigger className="my-2 w-full justify-between px-1 py-2">
        <span className="relative flex-1 text-left">{label}</span>
        {showDot && <PulseDot className="mr-2" />}
      </Accordion.Trigger>
      <Accordion.Content className="flex flex-col gap-4 px-1" asChild>
        {children}
        <div className="h-2" />
      </Accordion.Content>
    </Accordion.Item>
  )
}

export default function MapFilterSheet() {
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const defaultMapFilters = useAtomValue(aDefaultMapFilters)
  const [mapFilters, setMapFilters] = useAtom(aMapFilters)
  const [filters, setFilters] = useState(mapFilters)
  const { isEmbed } = useEmbed()
  const isSafari = useIsSafari()
  const [accordionState, setAccordionState] = useState<string[]>(['listing'])
  const setPersistedFilters = useSetAtom(aPersistedFilters)
  const setMapZoomNextListingLoad = useSetAtom(aMapZoomNextListingLoad)
  const setDrawnFeature = useSetAtom(aDrawnFeature)
  const setDrawIsOpen = useSetAtom(aMapDrawIsOpen)

  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useAtom(aFiltersPinned)
  const { lgDown } = useBreakpoint()

  const toggleSheetPinned = () => {
    setIsPinned(!isPinned)
  }

  useEffect(() => {
    if (lgDown && isPinned) {
      setIsPinned(false)
    }
  }, [lgDown, isPinned])

  const { companies } = useAdminCompaniesQuery(
    ['filter-sheet-active-companies'],
    {
      status: ModelStatus.active,
    },
    { enabled: !isEmbed },
    { tid: 1, slug: 1, name: 1 }
  )
  const { users: brokers } = useAdminUsersQuery(
    ['filter-sheet-active-brokers'],
    {
      status: ModelStatus.active,
      role: {
        $in: [UserRole.broker, UserRole.managingBroker],
      },
    },
    { enabled: !isEmbed },
    { tid: 1, slug: 1, firstName: 1, middleName: 1, lastName: 1 }
  )

  const prevAccordionStateRef = useRef<string[]>(accordionState)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const newlyOpened = accordionState.find(
      (item) => !prevAccordionStateRef.current.includes(item)
    )

    if (newlyOpened) {
      const timeout = setTimeout(() => {
        const triggers = Array.from(document.querySelectorAll('button'))
        const accordionTrigger = triggers.find((btn) => {
          const text = (btn.textContent || '').trim().toLowerCase()
          return text.includes(newlyOpened.toLowerCase())
        })

        if (accordionTrigger) {
          const accordionItem = accordionTrigger.closest(
            'div[class*="border-b"]'
          )
          if (accordionItem) {
            accordionItem.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
        }
      }, 100)

      prevAccordionStateRef.current = accordionState

      return () => clearTimeout(timeout)
    } else {
      prevAccordionStateRef.current = accordionState
    }
  }, [accordionState])

  useEffect(() => {
    if (!isOpen) {
      setFilters(mapFilters)
    }
  }, [isOpen, mapFilters, setFilters])

  const updateFilter = useCallback(
    (updates: Record<string, any>) => {
      const newFilters: { [key: string]: any } = { ...filters }

      Object.entries(updates).forEach(([key, value]) => {
        if (typeof value === 'undefined') {
          delete newFilters[key]
        } else {
          newFilters[key] = value
        }
      })

      // Clearing subtypes elsewhere caused issues
      if (
        'propertyTypes' in updates &&
        (!updates.propertyTypes || updates.propertyTypes.length === 0)
      ) {
        delete newFilters['propertySubtypes']
      } else if ('propertyTypes' in updates) {
        const normalizedPropertySubtypes = normalizeMapPropertySubtypeFilters(
          newFilters.propertyTypes,
          newFilters.propertySubtypes
        )

        if (normalizedPropertySubtypes) {
          newFilters.propertySubtypes = normalizedPropertySubtypes
        } else {
          delete newFilters['propertySubtypes']
        }
      }
      if ('types' in updates && updates.types.includes('sale') === false) {
        delete newFilters['business']
      }

      setFilters(newFilters)
      setPersistedFilters(newFilters)
    },
    [filters]
  )

  const updateMapFilters = () => {
    if ((filters.types || []).includes('sale') === false) {
      filters.prices = []
      filters.caps = []
    }
    if ((filters.types || []).includes('lease') === false) {
      filters.rates = []
      filters.sf = []
    }
    if ((filters.propertyTypes || []).includes('Industrial') === false) {
      INDUSTRIAL_FILTERS.forEach((key) => {
        // @ts-expect-error
        filters[key] = []
      })
    }
    if ((filters.propertyTypes || []).includes('Multifamily') === false) {
      MULTIFAMILY_FILTERS.forEach((key) => {
        // @ts-expect-error
        filters[key] = []
      })
    }
    setMapFilters(filters)
    setPersistedFilters(null)
    if (!isPinned) {
      setIsOpen(false)
    }
  }

  const resetMapFilters = () => {
    clearRememberedDrawnFeatureForBackNavigation()
    setDrawnFeature(null)
    setDrawIsOpen(false)
    setPersistedFilters(null)
    setAccordionState(['listing'])
    const filters = {
      lat: mapFilters.lat,
      lng: mapFilters.lng,
      z: mapFilters.z,
    }
    setMapFilters(filters)
    setTimeout(() => setFilters({ ...filters, ...defaultMapFilters }), 200)
    // Scroll to top
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }

  const different = useMemo(() => {
    return (
      getObjectDiff(
        omit(mapFilters, ['lat', 'lng', 'z']),
        omit(defaultMapFilters, ['lat', 'lng', 'z'])
      ).length > 0
    )
  }, [mapFilters])

  return (
    <Sheet
      modal={false}
      open={isPinned || isOpen}
      onOpenChange={(open) => {
        if (!isPinned) {
          setIsOpen(open)
          if (!open) {
            setFilters(mapFilters)
          }
        }
      }}>
      {!isPinned && (
        <ControlTooltip text="Filter and sort listings" side="bottom">
          <Sheet.Trigger asChild>
            <Button
              variant="map"
              size="map"
              className={cn(
                'relative shadow-md',
                user ? 'xl:w-auto xl:px-3' : 'w-auto px-3'
              )}>
              <LucideSlidersHorizontal className="h-5 w-5" />
              <span className={cn('ml-2', user && 'hidden xl:inline-flex')}>
                Filters
              </span>
              {different && !isEmbed && (
                <PulseDot className="absolute -right-0.5 -top-0.5" />
              )}
            </Button>
          </Sheet.Trigger>
        </ControlTooltip>
      )}
      <Sheet.Content
        className={cn('flex w-full flex-col', isPinned && '!duration-0')}
        side="right"
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation()
          }
        }}
        onCloseClick={(e) => {
          setIsOpen(false)
          setIsPinned(false)
        }}>
        <Sheet.Header>
          <Sheet.Description className="sr-only"></Sheet.Description>

          <div className="flex items-center justify-between gap-2">
            <Sheet.Title className="mt-0">
              Filter Listings{' '}
              <Button
                variant="link"
                size="none"
                className="p-0"
                onClick={() => {
                  if (isSafari) {
                    resetMapFilters()
                  }
                  resetMapFilters()
                  setMapZoomNextListingLoad(false)
                }}>
                (reset all)
              </Button>
            </Sheet.Title>
            <button
              className={cn(
                'absolute right-11 top-4 hidden rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary xl:block',
                isEmbed && '!hidden'
              )}
              onClick={() => toggleSheetPinned()}>
              {isPinned ? (
                <LucidePinOff className="h-4 w-4" />
              ) : (
                <LucidePin className="h-4 w-4" />
              )}
              <span className="sr-only">Pin Filters</span>
            </button>
          </div>
          {/* <Sheet.Description></Sheet.Description> */}
        </Sheet.Header>
        <div
          ref={scrollContainerRef}
          className="-mx-6 flex-1 overflow-auto px-5">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (isSafari) {
                updateMapFilters()
              }
              updateMapFilters()
            }}>
            <div className="space-y-4 px-1">
              <MapSort sort={filters.sort} updateFilter={updateFilter} />
              <MapSearchFilter
                search={filters.search}
                updateFilter={updateFilter}
              />
            </div>

            <Accordion
              type="multiple"
              value={accordionState}
              onValueChange={setAccordionState}>
              <FilterAccordion
                className="mt-0"
                label="Listing"
                filters={filters}
                keys={[
                  'propertyTypes',
                  'propertySubtypes',
                  // 'statuses',
                  // 'types',
                ]}>
                <>
                  <MapPropertyTypesFilter
                    propertyTypes={filters.propertyTypes || []}
                    updateFilter={updateFilter}
                  />
                  <MapPropertySubtypesFilter
                    propertySubtypes={filters.propertySubtypes || []}
                    propertyTypes={filters.propertyTypes || []}
                    updateFilter={updateFilter}
                  />
                  {user && (
                    <MapListingStatusFilter
                      statuses={filters.statuses || []}
                      updateFilter={updateFilter}
                    />
                  )}
                  <MapTypeFilter
                    types={filters.types || []}
                    updateFilter={updateFilter}
                  />
                  {(filters.types || []).includes('sale') && (
                    <MapBusinessListingFilter
                      business={filters.business}
                      updateFilter={updateFilter}
                    />
                  )}
                </>
              </FilterAccordion>

              <div
                className={cn(
                  'justify-center',
                  accordionState.length === 0 ||
                    (accordionState.length === 1 &&
                      accordionState[0] === 'listing')
                    ? 'hidden'
                    : 'flex'
                )}>
                <Button
                  className="-mb-4 flex gap-1 px-1"
                  size="sm"
                  variant="link"
                  onClick={() => setAccordionState(['listing'])}>
                  Collapse All
                  <LucideChevronUp className="h-4 w-4" />
                </Button>
              </div>

              {(filters.types || []).includes('sale') && (
                <FilterAccordion
                  label="Sale"
                  filters={filters}
                  keys={['prices', 'priceType', 'caps']}>
                  <MapSalePriceFilter
                    filterValues={filters.prices || []}
                    priceType={filters.priceType || 'total'}
                    updateFilter={updateFilter}
                  />

                  <MapMinMaxFilter
                    name="caps"
                    label="Cap Rate"
                    end="%"
                    filterValues={filters.caps || []}
                    updateFilter={updateFilter}
                  />
                </FilterAccordion>
              )}

              {(filters.types || []).includes('lease') && (
                <FilterAccordion
                  label="Lease"
                  filters={filters}
                  keys={['rates', 'sf']}>
                  <>
                    <MapRateFilter
                      filterValues={filters.rates || []}
                      rateType={filters.rateType || 'annual'}
                      updateFilter={updateFilter}
                    />
                    <MapMinMaxFilter
                      name="sf"
                      label="Available SF"
                      end="SF"
                      filterValues={filters.sf || []}
                      updateFilter={updateFilter}
                    />
                  </>
                </FilterAccordion>
              )}

              <FilterAccordion
                label="Building & Property"
                filters={filters}
                keys={['bsf', 'acres', 'lotSizeUnit', 'ceilingHeight']}>
                <>
                  <MapMinMaxFilter
                    name="bsf"
                    label="Building SF"
                    end="SF"
                    filterValues={filters.bsf || []}
                    updateFilter={updateFilter}
                  />
                  <MapLotSizeFilter
                    filterValues={filters.acres || []}
                    lotSizeUnit={filters.lotSizeUnit || 'acres'}
                    updateFilter={updateFilter}
                  />
                  <MapMinMaxFilter
                    name="ceilingHeight"
                    label="Ceiling Height"
                    filterValues={filters.ceilingHeight || []}
                    updateFilter={updateFilter}
                  />
                </>
              </FilterAccordion>

              {(filters.propertyTypes || []).includes('Industrial') && (
                <FilterAccordion
                  label="Industrial"
                  filters={filters}
                  keys={INDUSTRIAL_FILTERS}>
                  <MapIndustrialFilters
                    filters={filters}
                    updateFilter={updateFilter}
                  />
                </FilterAccordion>
              )}

              {(filters.propertyTypes || []).includes('Multifamily') && (
                <FilterAccordion
                  label="Multifamily"
                  filters={filters}
                  keys={MULTIFAMILY_FILTERS}>
                  <MapMinMaxFilter
                    name="totalUnits"
                    label="Total Units"
                    filterValues={filters.totalUnits || []}
                    updateFilter={updateFilter}
                  />
                </FilterAccordion>
              )}

              {organization!.options.singleCompany !== true && (
                <FilterAccordion
                  label="Location"
                  filters={filters}
                  keys={[
                    'addressSearch',
                    'zoning',
                    'submarkets',
                    'cities',
                    'counties',
                    'postalCodes',
                  ]}>
                  <>
                    <LocationFilters
                      filters={filters}
                      updateFilter={updateFilter}
                    />
                  </>
                </FilterAccordion>
              )}

              <FilterAccordion
                label="Dates"
                filters={filters}
                keys={['created', 'transacted', 'modified']}>
                <>
                  {organization!.options.appraisersOnly !== true && (
                    <MapDateFilter
                      name="created"
                      label="Listed Between"
                      filterValue={filters.created ?? []}
                      updateFilter={updateFilter}
                    />
                  )}

                  <MapDateFilter
                    name="modified"
                    label="Changed Between"
                    filterValue={filters.modified ?? []}
                    updateFilter={updateFilter}
                  />

                  {!isEmbed && (
                    <MapDateFilter
                      className="!mb-0"
                      name="transacted"
                      label="Transacted Between (Sold/Leased)"
                      filterValue={filters.transacted ?? []}
                      updateFilter={updateFilter}
                    />
                  )}
                </>
              </FilterAccordion>

              {(isEmbed === false ||
                organization!.options.singleCompany === true) && (
                <FilterAccordion
                  label="Members"
                  className="border-b-0"
                  filters={filters}
                  keys={['companies', 'brokers']}>
                  <>
                    {isEmbed === false &&
                      organization?.options.singleCompany !== true && (
                        <>
                          <MapCompaniesFilter
                            companies={companies}
                            filterCompanies={filters.companies}
                            updateFilter={updateFilter}
                          />
                          <MapBrokersFilter
                            brokers={brokers}
                            filterBrokers={filters.brokers}
                            updateFilter={updateFilter}
                          />
                        </>
                      )}
                    {organization!.options.singleCompany === true && (
                      <MapBrokersFilter
                        brokers={brokers}
                        filterBrokers={filters.brokers}
                        updateFilter={updateFilter}
                      />
                    )}
                  </>
                </FilterAccordion>
              )}
            </Accordion>
            <input className="hidden" type="submit" />
          </form>
        </div>
        <div className="flex gap-4 border-t pt-6">
          <Button
            onClick={() => {
              if (isSafari) {
                updateMapFilters()
              }
              updateMapFilters()
            }}>
            Update
          </Button>
          {!isPinned && (
            <Button
              variant="secondary"
              onClick={() => {
                setIsOpen(false)
              }}>
              Cancel
            </Button>
          )}
        </div>
      </Sheet.Content>
    </Sheet>
  )
}
